/**
 * L'encart du numéro de TVA du tiroir du panier (#23, snippets/cart-vat-number.liquid). Le client connecté l'ouvre par
 * « Add a VAT number » et le referme par « Cancel ». Le numéro part en JSON vers le proxy de l'app connecteur, que
 * Shopify signe avec l'identité du client et que reçoit le service de #24. Il répond {"ok": true, "status":
 * "valid"|"invalid"|"unavailable", "number"}, {"ok": false, "reason": "login"} quand le client n'est plus connecté,
 * {"ok": false, "reason": "invalid"} pour un numéro illisible, {"ok": false} sinon. Validé, le message remplace les
 * champs, et au rendu suivant du tiroir l'encart n'est plus rendu. Le tiroir reconstruit son contenu à chaque
 * changement du panier : l'encart est un élément personnalisé, qui se repose à chaque rendu.
 */
class CartVatNumber extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-vat-form]');
    // Le visiteur n'a que l'invitation à se connecter
    if (!this.form) return;
    this.folded = this.querySelector('[data-vat-folded]');
    this.message = this.querySelector('[data-vat-message]');
    this.querySelector('[data-vat-open]').addEventListener('click', () => this.toggle(true));
    this.querySelector('[data-vat-cancel]').addEventListener('click', () => this.toggle(false));
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  toggle(open) {
    this.folded.hidden = open;
    this.form.hidden = !open;
    this.message.hidden = true;
    if (open) this.form.elements.number.focus();
  }

  async submit(event) {
    event.preventDefault();
    // Les données se lisent avant que les champs soient désactivés : un champ désactivé n'en ferait plus partie.
    const donnees = JSON.stringify(Object.fromEntries(new FormData(this.form)));
    const champs = [...this.form.elements];
    const libelle = this.form.querySelector('[data-vat-label]');
    const texte = libelle.textContent;
    champs.forEach((champ) => { champ.disabled = true; });
    libelle.textContent = this.dataset.checking;
    this.message.hidden = true;
    let reponse = null;
    try {
      // Une réponse d'erreur porte aussi son JSON : le numéro illisible (422)
      const retour = await fetch(this.form.action, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: donnees });
      reponse = await retour.json();
    } catch {
      reponse = null;
    }
    champs.forEach((champ) => { champ.disabled = false; });
    libelle.textContent = texte;

    if (reponse?.ok === true && reponse.status === 'valid') {
      const valide = this.querySelector('[data-vat-valid]');
      valide.querySelector('[data-vat-valid-text]').textContent = this.dataset.validated.replace('[number]', reponse.number);
      this.form.hidden = true;
      valide.hidden = false;
      this.classList.add('is-valid');
      // La vitrine bascule vers le pays du numéro validé (#116, snippets/vat-country-switch.liquid), mais une fois le
      // tiroir refermé : la validation reste affichée tant qu'il est ouvert, comme sur la maquette
      const tiroir = this.closest('cart-drawer');
      const basculer = () => window.sydeVatCountry?.appliquer(reponse.number);
      if (!tiroir?.classList.contains('is-visible')) {
        basculer();
        return;
      }
      const observateur = new MutationObserver(() => {
        if (tiroir.classList.contains('is-visible')) return;
        observateur.disconnect();
        basculer();
      });
      observateur.observe(tiroir, { attributes: true, attributeFilter: ['class'] });
      return;
    }
    if (reponse?.reason === 'login') {
      // Déconnecté entre-temps : le message de connexion et « Log in » remplacent le formulaire
      this.querySelector('[data-vat-login-text]').textContent = this.dataset.loggedOut;
      this.form.hidden = true;
      this.querySelector('[data-vat-login]').hidden = false;
      return;
    }
    let cle = 'failure';
    if (reponse?.status === 'unavailable') cle = 'unavailable';
    else if (reponse?.status === 'invalid' || reponse?.reason === 'invalid') cle = 'invalid';
    this.message.querySelector('[data-vat-message-text]').textContent = this.dataset[cle];
    this.message.hidden = false;
  }
}
customElements.define('cart-vat-number', CartVatNumber);

// Au retour de la connexion (?vat-number), le tiroir s'ouvre de lui-même, et le paramètre quitte l'adresse pour qu'un
// rechargement ne le rouvre pas.
{
  const adresse = new URL(window.location.href);
  if (adresse.searchParams.has('vat-number')) {
    adresse.searchParams.delete('vat-number');
    history.replaceState(null, '', adresse);
    customElements.whenDefined('cart-drawer').then(() => document.querySelector('cart-drawer')?.open());
  }
}
